import { IHealthRecord } from "@interfaces";
import React, { memo, useEffect, useState } from "react";
import * as Yup from "yup";
import { useYup } from "@validations";
import { Input, Form, Row, Col, Radio, Button, Space, FormInstance, Select, message } from "antd";
import { useTranslation } from "react-i18next";

import {
  STEP_THREE,
  IUD,
  OTHER,
  UNFAVORABLE_FOR_INSERTION,
  VIA_RESULT,
  REFERRED,
  YES,
  IMPLANT,
  MALE,
  occasionData,
  Old_Client
} from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";

import { useNotify } from "@utils";
import { useHistory, useParams } from "react-router";
import { IconCheck, PlusColor, PlusIcon, SharedButton, SharedIconButton } from "@components";
import styled from "styled-components";
import { EditOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";
const btnSty = { background: "#00AA67" };
const btnPls = { display: "flex", alignItems: "center", justifyContent: "center" };

interface IProps {
  data: IHealthRecord;
  callback: (data: IHealthRecord, step: number) => void;
}
export const StepTwo = memo(({ data, callback }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { YupTwo } = useYup();
  const { id } = useParams<{ id: string }>();
  const { success, error } = useNotify();
  const [stepOneData, setStepOneData] = useState<IHealthRecord>({});
  const formRef = React.useRef<FormInstance>(null);

  const [longTerm, setLongTerm] = useState<boolean>(false);
  const [ccp, setCcp] = useState<boolean>(false);
  const [ayfhs, setAyfhs] = useState<boolean>(false);
  const [gbv, setGbv] = useState<boolean>(false);
  const [secondForm] = Form.useForm();
  const formTwoSchema = Yup.object().shape(YupTwo);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (data) {
      secondForm.setFieldsValue({
        ...data,
      });
    }
    setStepOneData(data);
  }, [data]);
  const onFinish = async (values: IHealthRecord) => {
    try {
      if (!id) {
        if (!allowBirthSpace()) {
          if (!birth_spacing_parity || !ccp_awareness) {
            console.log("one");
            throw new Error("Please fill the form");
          }
        }
        if (checkAge()) {
          if (!ayfhs_sti) {
            console.log("two");
            throw new Error("Please fill the form");  
          }
        }
        if (!gbv_awareness) {
          console.log("three");
          throw new Error("Please fill the form");
        }
      }
      await callback(values, STEP_THREE);
    } catch (err: any) {
      console.log(err);
      error(err.message);
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const clearBirthSpacingInfo = () => {
    secondForm.setFieldsValue({
      ...{
        birth_spacing_parity: "",
        birth_spacing_occasion: "",
        birth_spacing_user_type: "",
        birth_spacing_main_method_for_now: "",
        birth_spacing_iud_removal: "",
        birth_spacing_iud_removal_reason: "",
        birth_spacing_implant_removal: "",
        birth_spacing_implant_removal_reason: "",
        birth_spacing_implant_removal_reason_other: "",
        birth_spacing_insertion: "",
        birth_spacing_unfavorable_medical_condition: "",
      },
    });
  };

  const clearCCPInfo = () => {
    secondForm.setFieldsValue({
      ...{
        ccp_awareness: "",
        ccp_screening: "",
        ccp_screening_result: "",
        ccp_treatment: "",
        ccp_treatment_referred_site: "",       
      },
    });
  };

  const clearAYFHSInfo = () => {
    secondForm.setFieldsValue({
      ...{
        ayfhs_sti: "",
        ayfhs_birth_spacing: "",
        ayfhs_rh_problem: "",    
      },
    });
  };

  const clearGBVInfo = () => {
    secondForm.setFieldsValue({
      ...{
        gbv_awareness: "",
        gbv_hotline: "",
      },
    });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo);
  };
  const birth_spacing_user_type: string = Form.useWatch("birth_spacing_user_type", secondForm);
  const birth_spacing_main_method_for_now: string = Form.useWatch(
    "birth_spacing_main_method_for_now",
    secondForm
  );
  const birth_spacing_iud_removal: string = Form.useWatch("birth_spacing_iud_removal", secondForm);
  const birth_spacing_iud_removal_reason: string = Form.useWatch(
    "birth_spacing_iud_removal_reason",
    secondForm
  );
  const birth_spacing_implant_removal: string = Form.useWatch(
    "birth_spacing_implant_removal",
    secondForm
  );
  const birth_spacing_implant_removal_reason: string = Form.useWatch(
    "birth_spacing_implant_removal_reason",
    secondForm
  );
  const birth_spacing_insertion: string = Form.useWatch("birth_spacing_insertion", secondForm);
  const ccp_screening: string = Form.useWatch("ccp_screening", secondForm);
  const ccp_screening_result: string = Form.useWatch("ccp_screening_result", secondForm);
  const ccp_treatment: string = Form.useWatch("ccp_treatment", secondForm);
  const client_age: number = Form.useWatch("client_age", secondForm);
  const client_gender: string = Form.useWatch("client_gender", secondForm);

  const gbv_awareness: string = Form.useWatch("gbv_awareness", secondForm);
  const ayfhs_sti: string = Form.useWatch("ayfhs_sti", secondForm);
  const birth_spacing_parity: string = Form.useWatch("birth_spacing_parity", secondForm);
  const ccp_awareness: string = Form.useWatch("ccp_awareness", secondForm);

  const checkAge = () => client_age > 9 && client_age < 25;
  const allowBirthSpace = () => client_gender === MALE;

  return (
    <Form
      form={secondForm}
      name="basic"
      initialValues={stepOneData}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
    >
      <StyledPageTitle>
        <h4>{t("page.rh_services")}</h4>
      </StyledPageTitle>
      <StyledService
        className={allowBirthSpace() ? "disabled" : ""}
      >
        <p>{t("page.Long_Term_Birth_Spacing_Service")}</p>
        <div className="action">
          <Button
            style={btnPls}
            type={longTerm ? "primary" : "default"}
            icon={longTerm ? <IconCheck /> : <PlusColor />}
            size={"small"}
            onClick={() => setLongTerm(!longTerm)}
          />
          <SharedIconButton style={btnSty} type="primary" onClick={() => clearBirthSpacingInfo()} icon={<CloseOutlined />} size={"small"} />
        </div>
      </StyledService>

      {longTerm && (
        <StyledSection>
          <Row gutter={{ md: 54, lg: 54 }}>
            <Col lg={12} md={24} sm={24} xs={24}>
              <Form.Item
                label={t("page.Parity")}
                name="birth_spacing_parity"
                rules={[{ required: true }]}
              >
                <Input type="text" />
              </Form.Item>
            </Col>
            <Col lg={12} md={24} sm={24} xs={24}>
              <Form.Item
                label={t("page.Occasion")}
                name="birth_spacing_occasion"
                rules={[{ required: true }]}
              >
                <Select>
                  {occasionData.map((item) => (
                    <Select.Option value={item} key={item}>
                      {item}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Form.Item
              name={"birth_spacing_user_type"}
              label={t("page.Client_Birth_Spacing_Usage")}
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space className="titale-optoin">
                  <Radio value={"New Client"}>{"New Client"}</Radio>
                  <Radio value={"Old Client"}>{"Old Client"}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </Row>
          {birth_spacing_user_type === "Old Client" && (
            <StyledGroupSection>
              <Form.Item
                name={"birth_spacing_main_method_for_now"}
                label={t("page.What_is_the_main_birth_spacing_method_using_now")}
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Space direction="vertical">
                    <Radio value={"OC pills"}>{"OC pills"}</Radio>
                    <Radio value={"Condoms"}>{"Condoms"}</Radio>
                    <Radio value={"Injection"}>{"Injection"}</Radio>
                    <Radio value={"DMPA SC"}>{"DMPA SC"}</Radio>
                    <Radio value={"IUD"}>{"IUD"}</Radio>
                    {birth_spacing_main_method_for_now === IUD && (
                      <StyledGroupSection>
                        <Form.Item
                          name={"birth_spacing_iud_removal"}
                          label={t("page.IUD_Removal")}
                          rules={[{ required: true }]}
                        >
                          <Radio.Group>
                            <Space>
                              <Radio value={"Yes"}>{"Yes"}</Radio>
                              <Radio value={"No"}>{"No"}</Radio>
                            </Space>
                          </Radio.Group>
                        </Form.Item>
                        {birth_spacing_iud_removal === YES && (
                          <StyledGroupSection>
                            <Form.Item
                              name={"birth_spacing_iud_removal_reason"}
                              label={t("page.Reason_for_IUD_Removal")}
                              rules={[{ required: true }]}
                            >
                              <Radio.Group>
                                <Space direction="vertical">
                                  <Radio value={"It was expired"}>{"It was expired"}</Radio>
                                  <Radio value={"Client is pregnant"}>{"Client is pregnant"}</Radio>
                                  <Radio value={"Side Effect (Heavy Menstrual Bleeding)"}>
                                    {"Side Effect (Heavy Menstrual Bleeding)"}
                                  </Radio>
                                  <Radio value={"Client has STD (Sexually Transmitted Disease)"}>
                                    {"Client has STD (Sexually Transmitted Disease)"}
                                  </Radio>
                                  <Radio value={"Client wants pregnancy"}>
                                    {"Client wants pregnancy"}
                                  </Radio>
                                  <Radio value={"IUD was out of uterus"}>
                                    {"IUD was out of uterus"}
                                  </Radio>
                                  <Radio value={"Others"}>{"Others"}</Radio>
                                </Space>
                              </Radio.Group>
                            </Form.Item>
                            {birth_spacing_iud_removal_reason === OTHER && (
                              <StyledGroupSection>
                                <Form.Item
                                  name="birth_spacing_iud_removal_reason_other"
                                  label={t("page.What_is_the_other_reason_for_IUD_removal")}
                                  rules={[{ required: true }]}
                                >
                                  <Input type="text" />
                                </Form.Item>
                              </StyledGroupSection>
                            )}
                          </StyledGroupSection>
                        )}
                      </StyledGroupSection>
                    )}
                    <Radio value={"Implant"}>{"Implant"}</Radio>
                    {birth_spacing_main_method_for_now === IMPLANT && (
                      <StyledGroupSection>
                        <Form.Item
                          name="birth_spacing_implant_removal"
                          label={t("page.Implant_Removal")}
                          rules={[{ required: true }]}
                        >
                          <Space>
                            <Radio.Group>
                              <Radio value={"Yes"}>{"Yes"}</Radio>
                              <Radio value={"No"}>{"No"}</Radio>
                            </Radio.Group>
                          </Space>
                        </Form.Item>
                        {birth_spacing_implant_removal === YES && (
                          <StyledGroupSection>
                            <Form.Item
                              name="birth_spacing_implant_removal_reason"
                              label={t("page.Reason_for_IUD_Removal")}
                              rules={[{ required: true }]}
                            >
                              <Radio.Group>
                                <Space direction="vertical">
                                  <Radio value={"It was expired"}>{"It was expired"}</Radio>
                                  <Radio value={"Client is pregnant"}>{"Client is pregnant"}</Radio>
                                  <Radio value={"Side Effect (Heavy Menstrual Bleeding)"}>
                                    {"Side Effect (Heavy Menstrual Bleeding)"}
                                  </Radio>
                                  <Radio value={"Client has STD (Sexually Transmitted Disease)"}>
                                    {"Client has STD (Sexually Transmitted Disease)"}
                                  </Radio>
                                  <Radio value={"Client wants pregnancy"}>
                                    {"Client wants pregnancy"}
                                  </Radio>
                                  <Radio value={"IUD was out of uterus"}>
                                    {"IUD was out of uterus"}
                                  </Radio>
                                  <Radio value={"Others"}>{"Others"}</Radio>
                                </Space>
                              </Radio.Group>
                            </Form.Item>
                            {birth_spacing_implant_removal_reason === OTHER && (
                              <StyledGroupSection>
                                <Form.Item
                                  name={"birth_spacing_implant_removal_reason_other"}
                                  label={t("page.What_is_the_other_reason_for_Implant_removal")}
                                  rules={[{ required: true }]}
                                >
                                  <Input type="text" />
                                </Form.Item>
                              </StyledGroupSection>
                            )}
                          </StyledGroupSection>
                        )}
                      </StyledGroupSection>
                    )}
                    <Radio value={"Currently Stopped or None"}>{"Currently Stopped or None"}</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </StyledGroupSection>
          )}
          <StyledOuterGroupSection>
            <Form.Item
              name={"birth_spacing_insertion"}
              label={t("page.Insertion")}
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space direction="vertical">
                  <Radio value={"IUD Insertion"}>{"IUD Insertion"}</Radio>
                  <Radio value={"Implant insertion"}>{"Implant insertion"}</Radio>
                  <Radio value={"No insertion"}>{"No insertion"}</Radio>
                  <Radio value={"Unfavorable for insertion"}>{"Unfavorable for insertion"}</Radio>
                  {birth_spacing_insertion === UNFAVORABLE_FOR_INSERTION && (
                    <StyledGroupSection>
                      <Form.Item
                        name="birth_spacing_unfavorable_medical_condition"
                        label="Which medical condition is unfavorable?"
                        rules={[{ required: true }]}
                      >
                        <Input type="text" placeholder="Enter unfavorable condition." />
                      </Form.Item>
                    </StyledGroupSection>
                  )}
                </Space>
              </Radio.Group>
            </Form.Item>
          </StyledOuterGroupSection>
        </StyledSection>
      )}
      <StyledService
        className={allowBirthSpace() ? "disabled" : ""}
      >
        <p>{t("page.Cervical_Cancer_Prevention_Services_CCP")}</p>
        <div className="action">
          <Button
            style={btnPls}
            type={ccp ? "primary" : "default"}
            icon={ccp ? <IconCheck /> : <PlusColor />}
            size={"small"}
            onClick={() => setCcp(!ccp)}
          />
          <SharedIconButton style={btnSty} type="primary" onClick={() => clearCCPInfo()} icon={<CloseOutlined />} size={"small"} />
        </div>
      </StyledService>
      {ccp && (
        <StyledSection>
          <StyledOuterGroupSection>
            <Form.Item
              name={"ccp_awareness"}
              label={"Do you provide CCP awareness education session to the client?"}
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space>
                  <Radio value={"Yes"}>{"Yes"}</Radio>
                  <Radio value={"No"}>{"No"}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </StyledOuterGroupSection>
          <StyledOuterGroupSection>
            <Form.Item
              name={"ccp_screening"}
              label="Do you provide CCP screening to the client?"
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space>
                  <Radio value={"Yes"}>{"Yes"}</Radio>
                  <Radio value={"No"}>{"No"}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </StyledOuterGroupSection>
          {ccp_screening === YES && (
            <StyledGroupSection>
              <Form.Item
                name="ccp_screening_result"
                label="CCP Screening Result"
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Space direction="vertical">
                    <Radio value={"VIA Result+"}>{"VIA Result+"}</Radio>
                    <Radio value={"VIA Result-"}>{"VIA Result-"}</Radio>
                    {ccp_screening_result === VIA_RESULT && (
                      <StyledGroupSection>
                        <Form.Item
                          label="CCP Treatment"
                          name={"ccp_treatment"}
                          rules={[{ required: true }]}
                        >
                          <Radio.Group>
                            <Space direction="vertical">
                              <Radio value={"Cryotherapy"}>{"Cryotherapy"}</Radio>
                              <Radio value={"Thermal Ablation"}>{"Thermal Ablation"}</Radio>
                              <Radio value={"Referred"}>{"Referred"}</Radio>
                              {ccp_treatment === REFERRED && (
                                <StyledGroupSection>
                                  <Form.Item
                                    name="ccp_treatment_referred_site"
                                    label="Referral Site"
                                    rules={[{ required: true }]}
                                  >
                                    <Input type="text" placeholder="Enter the referral site name" />
                                  </Form.Item>
                                </StyledGroupSection>
                              )}
                            </Space>
                          </Radio.Group>
                        </Form.Item>
                      </StyledGroupSection>
                    )}
                  </Space>
                </Radio.Group>
              </Form.Item>
            </StyledGroupSection>
          )}
        </StyledSection>
      )}
      <StyledService className={!checkAge() ? "disabled" : ""} >
        <p>{t("page.Adolescent_Youth_Friendly_Health_Services_AYFHS_10_24")}</p>
        <div className="action">
          <Button
            style={btnPls}
            type={ayfhs ? "primary" : "default"}
            icon={ayfhs ? <IconCheck /> : <PlusColor />}
            size={"small"}
            onClick={() => setAyfhs(!ayfhs)}
          />
          <SharedIconButton style={btnSty} type="primary" onClick={() => clearAYFHSInfo()} icon={<CloseOutlined />} size={"small"} />
        </div>
      </StyledService>
      {ayfhs && (
        <StyledSection>
          <StyledOuterGroupSection>
            <Form.Item
              name="ayfhs_sti"
              label={"AYFHS Services – STI?"}
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space>
                  <Radio value={"Yes"}>{"Yes"}</Radio>
                  <Radio value={"No"}>{"No"}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </StyledOuterGroupSection>
          <StyledOuterGroupSection>
            <Form.Item
              name="ayfhs_birth_spacing"
              label={"AYFHS Services – Birth Spacing?"}
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space>
                  <Radio value={"Yes"}>{"Yes"}</Radio>
                  <Radio value={"No"}>{"No"}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </StyledOuterGroupSection>
          <StyledOuterGroupSection>
            <Form.Item
              name="ayfhs_rh_problem"
              label={"AYFHS Services – Other RH Problems?"}
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space>
                  <Radio value={"Yes"}>{"Yes"}</Radio>
                  <Radio value={"No"}>{"No"}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </StyledOuterGroupSection>
        </StyledSection>
      )}
      <StyledService >
        <p>{t("page.Gender_Based_Violence_GBV")}</p>
        <div className="action">
          <Button
            style={btnPls}
            type={gbv ? "primary" : "default"}
            icon={gbv ? <IconCheck /> : <PlusColor />}
            size={"small"}
            onClick={() => setGbv(!gbv)}
          />
          <SharedIconButton style={btnSty} type="primary" onClick={() => clearGBVInfo()} icon={<CloseOutlined />} size={"small"} />
        </div>
      </StyledService>
      {gbv && (
        <StyledSection>
          <StyledOuterGroupSection>
            <Form.Item
              name="gbv_awareness"
              label={"Do you provide GBV awareness to client?"}
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space>
                  <Radio value={"Yes"}>{"Yes"}</Radio>
                  <Radio value={"No"}>{"No"}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </StyledOuterGroupSection>
          <StyledOuterGroupSection>
            <Form.Item
              name="gbv_hotline"
              label={"Do you provide Hotline/Helpline to client?"}
              rules={[{ required: true }]}
            >
              <Radio.Group>
                <Space>
                  <Radio value={"Yes"}>{"Yes"}</Radio>
                  <Radio value={"No"}>{"No"}</Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
          </StyledOuterGroupSection>
        </StyledSection>
      )}
      <Row gutter={{ md: 54, lg: 54 }}>
        <Col lg={12} md={24} sm={24} xs={24}></Col>
        <Col lg={12} md={24} sm={24} xs={24}>
          <StyledFormAction>
            <SharedButton type="primary" htmlType="submit" size="middle">
              {t("page.next")}
            </SharedButton>
          </StyledFormAction>
        </Col>
      </Row>
      <StyledHiddenRow>
        <Form.Item name={"client_age"}>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name={"client_gender"}>
          <Input type="hidden" />
        </Form.Item>
      </StyledHiddenRow>
    </Form>
  );
});

const StyledHiddenRow = styled.div`
  display: none;
`;

const StyledFormAction = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 40px;
  margin: 20px 0;
`;
const StyledService = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: 1.5px solid #f06d24;
  border-radius: 5px;
  padding: 10px;
  margin: 16px 0;
  // cursor: pointer;
  .action {
    display: flex;
    column-gap: 10px;
    align-items: center;
  }
  &.disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.4;
  }
`;
const StyledSection = styled.section`
  margin-top: 16px;
  padding: 30px 32px;
  background: #fff;
  border-radius: 5px;
  .ant-space {
    margin: 14px 0;
    width: 100%;
  }
  .ant-radio-group {
    width: 100%;
  }
`;
const StyledGroupSection = styled.section`
  padding: 30px;
  border: 1px solid #c1c1c1;
  background-color: #f6f6f6;
  border-radius: 6px;
  .titale-optoin {
    margin-bottom: 16px;
  }

  p {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    color: #222222;
  }
`;
const StyledOuterGroupSection = styled.section`
  margin: 14px 0;
  p {
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    color: #222222;
  }
`;
const StyledPageTitle = styled.header`
  margin-bottom: 40px;
  h4 {
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
  }
`;
